import { useMemo, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import { formatLocalNumber, operatorById } from '@/utils/sendMoney';
import type { MoneyRecipient } from '@/types/portal';

interface ManageFavouritesStepProps {
  allRecipients: MoneyRecipient[];
  favouriteIds: Set<string>;
  dialCode: string;
  onToggle: (id: string) => void;
  /** Add a brand-new number as a favourite */
  onAddNumber: (number: string, dialCode: string) => void;
  onDone: () => void;
}

/**
 * "Manage favourites" — full list of saved recipients with a star toggle on
 * each row, plus a quick-add field for new numbers.
 */
export default function ManageFavouritesStep({
  allRecipients,
  favouriteIds,
  dialCode,
  onToggle,
  onAddNumber,
  onDone,
}: ManageFavouritesStepProps) {
  const [search, setSearch] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [addMode, setAddMode] = useState(false);
  const [addedMsg, setAddedMsg] = useState('');

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase();
    if (!text) return allRecipients;
    return allRecipients.filter(
      (r) =>
        r.name.toLowerCase().includes(text) ||
        r.number.includes(text) ||
        formatLocalNumber(r.number, dialCode).replace(/\s/g, '').includes(text),
    );
  }, [search, allRecipients, dialCode]);

  const favourites = useMemo(() => filtered.filter((r) => favouriteIds.has(r.id)), [filtered, favouriteIds]);
  const others = useMemo(() => filtered.filter((r) => !favouriteIds.has(r.id)), [filtered, favouriteIds]);

  const handleAdd = () => {
    const digits = newNumber.replace(/\D/g, '');
    if (digits.length < 7) return;
    onAddNumber(digits, dialCode);
    setNewNumber('');
    setAddMode(false);
    setAddedMsg(`${dialCode} ${digits} added to favourites.`);
    setTimeout(() => setAddedMsg(''), 3000);
  };

  const recipientRow = (r: MoneyRecipient) => {
    const isFav = favouriteIds.has(r.id);
    return (
      <li key={r.id} className="flex items-center gap-3 py-2.5">
        <Avatar src={r.avatar} alt={r.name} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground-950">{r.name}</span>
          <span className="block truncate text-xs text-foreground-500">
            {formatLocalNumber(r.number, dialCode)} · {operatorById(r.operatorId)?.name ?? '—'}
          </span>
        </span>
        <button
          type="button"
          onClick={() => onToggle(r.id)}
          aria-label={isFav ? `Remove ${r.name} from favourites` : `Add ${r.name} to favourites`}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-background-100"
        >
          {isFav ? (
            <i className="ri-star-fill text-lg text-accent-500" />
          ) : (
            <i className="ri-star-line text-lg text-foreground-400" />
          )}
        </button>
      </li>
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading text-lg font-semibold text-foreground-950">
          Manage favourites
        </h3>
        <p className="mt-0.5 text-xs text-foreground-500">
          Star a recipient to pin them to the top of your contacts list.
        </p>
      </div>

      {/* Search bar */}
      <div className="relative">
        <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-base text-foreground-400">
          <i className="ri-search-line" />
        </span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts"
          className="h-10 w-full rounded-full border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        />
      </div>

      {/* Success flash */}
      {addedMsg ? (
        <div className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">
          <i className="ri-checkbox-circle-line text-accent-600" />
          {addedMsg}
        </div>
      ) : null}

      {/* Favourites section */}
      {favourites.length > 0 ? (
        <div>
          <p className="mb-1 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            Favourited
          </p>
          <ul className="divide-y divide-background-100">{favourites.map(recipientRow)}</ul>
        </div>
      ) : null}

      {/* All other contacts */}
      {others.length > 0 ? (
        <div>
          <p className="mb-1 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            {favourites.length > 0 ? 'Other contacts' : 'All contacts'}
          </p>
          <ul className="divide-y divide-background-100">{others.map(recipientRow)}</ul>
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <div className="py-6 text-center text-sm text-foreground-400">No contacts match your search.</div>
      ) : null}

      {/* Add a new number */}
      <div className="border-t border-background-100 pt-4">
        {addMode ? (
          <div className="space-y-3">
            <p className="text-xs font-medium text-foreground-600">
              Add a new number to favourites
            </p>
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-sm text-foreground-500">{dialCode}</span>
              <input
                value={newNumber}
                inputMode="numeric"
                onChange={(e) => setNewNumber(e.target.value.replace(/\D/g, ''))}
                placeholder="712 345 678"
                className="h-9 flex-1 border-b border-background-300 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
              />
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={newNumber.replace(/\D/g, '').length < 7}
              >
                Add
              </Button>
              <button
                type="button"
                onClick={() => { setAddMode(false); setNewNumber(''); }}
                className="cursor-pointer text-xs text-foreground-400 transition-colors hover:text-foreground-700"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddMode(true)}
            className="flex cursor-pointer items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100">
              <i className="ri-add-line text-sm text-primary-700" />
            </span>
            Add a new number
          </button>
        )}
      </div>

      {/* Done */}
      <div className="border-t border-background-100 pt-2">
        <Button variant="secondary" size="sm" onClick={onDone} className="w-full">
          Done
        </Button>
      </div>
    </div>
  );
}